import React, { useState } from 'react';
import Layout from '../components/Layout';
import { SITE } from '../siteInfo';
import { useLang } from '../i18n';

// Submitted to Netlify Forms; the matching static form lives in public/index.html
const FORM_NAME = 'contact';
const EMPTY_FORM = { name: '', company: '', email: '', phone: '', message: '', 'bot-field': '' };

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-gray-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20';
const labelClass = 'block mb-1 text-sm font-semibold text-gray-800';

const TEXT = {
  tr: {
    title: 'İletişim',
    description: 'AKKA Demir Çelik iletişim bilgileri: adres, telefon ve e-posta. Lazer kesim ve imalat projeleriniz için teklif isteyin.',
    heading: 'İletişim',
    lead: 'Projeniz veya fiyat teklifi için bize yazın, en kısa sürede dönüş yapalım.',
    honeypot: 'Bu alanı boş bırakın:',
    name: 'Ad Soyad *',
    company: 'Firma',
    email: 'E-posta *',
    phone: 'Telefon',
    message: 'Mesajınız *',
    sent: 'Mesajınız gönderildi. Teşekkür ederiz, en kısa sürede size dönüş yapacağız.',
    errorStart: 'Mesajınız gönderilemedi. Lütfen tekrar deneyin veya bize',
    errorPhone: 'telefonla',
    errorOr: 'ya da',
    errorEmail: 'e-posta ile',
    errorEnd: ' ulaşın.', // the other languages end with a bare full stop
    send: 'Gönder',
    sending: 'Gönderiliyor...',
    address: 'Adres',
    directions: 'Yol tarifi al',
    phoneHeading: 'Telefon',
    emailHeading: 'E-posta',
    country: '',
    mapTitle: 'AKKA Demir Çelik konumu',
  },
  en: {
    title: 'Contact',
    description: 'AKKA Demir Çelik contact details: address, phone and email. Request a quote for your laser cutting and fabrication projects.',
    heading: 'Contact',
    lead: 'Write to us about your project or request a quote, and we will get back to you as soon as possible.',
    honeypot: 'Leave this field empty:',
    name: 'Full name *',
    company: 'Company',
    email: 'Email *',
    phone: 'Phone',
    message: 'Your message *',
    sent: 'Your message has been sent. Thank you, we will get back to you shortly.',
    errorStart: 'Your message could not be sent. Please try again or reach us',
    errorPhone: 'by phone',
    errorOr: 'or',
    errorEmail: 'by email',
    errorEnd: '.',
    send: 'Send',
    sending: 'Sending...',
    address: 'Address',
    directions: 'Get directions',
    phoneHeading: 'Phone',
    emailHeading: 'Email',
    country: ', Türkiye',
    mapTitle: 'AKKA Demir Çelik location',
  },  de: {
    title: 'Kontakt',
    description: 'Kontaktdaten von AKKA Demir Çelik: Adresse, Telefon und E-Mail. Fordern Sie ein Angebot für Ihre Laserschneid- und Fertigungsprojekte an.',
    heading: 'Kontakt',
    lead: 'Schreiben Sie uns zu Ihrem Projekt oder fordern Sie ein Angebot an. Wir melden uns so schnell wie möglich.',
    honeypot: 'Dieses Feld bitte leer lassen:',
    name: 'Vor- und Nachname *',
    company: 'Firma',
    email: 'E-Mail *',
    phone: 'Telefon',
    message: 'Ihre Nachricht *',
    sent: 'Ihre Nachricht wurde gesendet. Vielen Dank, wir melden uns in Kürze bei Ihnen.',
    errorStart: 'Ihre Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut oder erreichen Sie uns',
    errorPhone: 'telefonisch',
    errorOr: 'oder',
    errorEmail: 'per E-Mail',
    errorEnd: '.',
    send: 'Senden',
    sending: 'Wird gesendet...',
    address: 'Adresse',
    directions: 'Route planen',
    phoneHeading: 'Telefon',
    emailHeading: 'E-Mail',
    country: ', Türkei',
    mapTitle: 'Standort von AKKA Demir Çelik',
  },
};

const Contact = () => {
  const lang = useLang();
  const t = TEXT[lang];
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ 'form-name': FORM_NAME, ...form }).toString(),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setForm(EMPTY_FORM);
      setStatus('sent');
    } catch (error) {
      console.error('Form gönderilemedi:', error);
      setStatus('error');
    }
  };

  const mapSrc = `https://maps.google.com/maps?q=${SITE.lat},${SITE.lng}&z=15&hl=${lang}&output=embed`;
  const directionsHref = `https://www.google.com/maps/dir/?api=1&destination=${SITE.lat},${SITE.lng}`;

  return (
    <Layout title={t.title} description={t.description}>
      <div className="bg-white">
        <div className="container mx-auto px-4 py-12 max-w-6xl">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mt-0 mb-2">{t.heading}</h1>
          <div className="w-24 h-1 bg-blue-600 rounded mb-4"></div>
          <p className="text-lg text-gray-600 mb-10">{t.lead}</p>

          <div className="grid gap-12 lg:grid-cols-5">
            <form
              name={FORM_NAME}
              method="POST"
              onSubmit={handleSubmit}
              className="lg:col-span-3 space-y-5"
            >
              <p hidden>
                <label>
                  {t.honeypot} <input name="bot-field" value={form['bot-field']} onChange={handleChange} />
                </label>
              </p>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className={labelClass}>{t.name}</label>
                  <input id="name" name="name" required autoComplete="name" value={form.name} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label htmlFor="company" className={labelClass}>{t.company}</label>
                  <input id="company" name="company" autoComplete="organization" value={form.company} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label htmlFor="email" className={labelClass}>{t.email}</label>
                  <input id="email" name="email" type="email" required autoComplete="email" value={form.email} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label htmlFor="phone" className={labelClass}>{t.phone}</label>
                  <input id="phone" name="phone" type="tel" autoComplete="tel" value={form.phone} onChange={handleChange} className={inputClass} />
                </div>
              </div>

              <div>
                <label htmlFor="message" className={labelClass}>{t.message}</label>
                <textarea id="message" name="message" required rows={6} value={form.message} onChange={handleChange} className={inputClass} />
              </div>

              {status === 'sent' && (
                <p role="status" className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-green-800">
                  {t.sent}
                </p>
              )}
              {status === 'error' && (
                <p role="alert" className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-red-800">
                  {t.errorStart}{' '}
                  <a href={SITE.phoneHref} className="font-semibold underline text-red-800">{t.errorPhone}</a> {t.errorOr}{' '}
                  <a href={`mailto:${SITE.email}`} className="font-semibold underline text-red-800">{t.errorEmail}</a>
                  {t.errorEnd}
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'sending'}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold rounded-lg transition duration-300"
              >
                {status === 'sending' ? t.sending : t.send}
              </button>
            </form>

            <aside className="lg:col-span-2">
              <div className="rounded-xl bg-gray-50 border border-gray-200 p-6 space-y-5">
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mt-0 mb-1">{t.address}</h2>
                  <address className="not-italic text-gray-900">
                    {SITE.street}<br />{SITE.city}{t.country}
                  </address>
                  <a href={directionsHref} target="_blank" rel="noopener noreferrer" className="inline-block mt-1 text-sm font-semibold text-blue-700 hover:text-blue-800">
                    {t.directions} &rarr;
                  </a>
                </div>
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mt-0 mb-1">{t.phoneHeading}</h2>
                  <a href={SITE.phoneHref} className="text-lg font-semibold text-gray-900 hover:text-blue-700">{SITE.phone}</a>
                </div>
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mt-0 mb-1">{t.emailHeading}</h2>
                  <a href={`mailto:${SITE.email}`} className="text-lg font-semibold text-gray-900 hover:text-blue-700 break-all">{SITE.email}</a>
                </div>
              </div>
            </aside>
          </div>
        </div>

        <iframe
          title={t.mapTitle}
          src={mapSrc}
          className="block w-full h-80 md:h-96 border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </div>
    </Layout>
  );
};

export default Contact;
